/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";

import { Button } from "react-bootstrap";
import DocFilingApi from "../../../api/DocFilingApi";
import { FileSizes } from "../../../config/app-constants";
import {
  EActionItem,
  EToastvariant,
  IActiveMDTTabs,
  IDocUploadSection,
  IFilingDeleteDoc,
  IUploadedDocument
} from "../../../config/interfaces";
import { useAppDispatch, useAppSelector } from "../../../redux/Store";
import { ITRFilingSelector } from "../../../redux/slice/ITRFilingSlice";
import { loggedInUserSelector } from "../../../redux/slice/LoggedInUserSlice";
import { showToastMsg } from "../../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../../services";
import FileUploadDropzone from "../../FileUploadDropzone";
import { UploadedDocument } from "../../itr-filing/assisted";
import { ConfirmationModal } from "../../modals";

type Props = {
	activeMDT: IActiveMDTTabs;
};

// Update this array to add/remove a section
const DocumentSections = [
	{ docKey: "form_16", title: "Form 16" },
	{ docKey: "tds", title: "TDS Form" },
	{ docKey: "bank_stmt", title: "Bank statements" },
	{ docKey: "gst", title: "GST Returns" },
	{ docKey: "housing_loan", title: "Housing loan interest certificate" },
	{ docKey: "form_b12", title: "Form B12" },
	{ docKey: "other_files", title: "Other files" }
];

const DocumentsTab: React.FC<Props> = ({ activeMDT }: Props) => {
	useEffect(() => {
		if (activeMDT === "documents") {
			getAllEfilingDocuments();
		}
	}, [activeMDT]);

	const dispatch = useAppDispatch();
	const { loggedInUser } = useAppSelector(loggedInUserSelector);
	const { currentFilingUUID, currentFilingData } = useAppSelector(ITRFilingSelector);

	const initialSections: IDocUploadSection = useMemo(() => {
		let initDocs: IDocUploadSection = {};
		DocumentSections?.forEach((section) => {
			initDocs[`${section.docKey}`] = {
				docKey: `${section.docKey}`,
				title: `${section.title}`,
				documents: []
			};
		});
		return initDocs;
	}, []);

	const initAddDocButton = useMemo(() => {
		let initAddDoc: { [key: string]: boolean } = {};
		DocumentSections?.forEach((section) => {
			initAddDoc[`${section.docKey}`] = false;
		});
		return initAddDoc;
	}, []);

	const [showAddDocButton, setShowAddDocButton] = useState<{ [key: string]: boolean }>(
		initAddDocButton
	);
	const [uploadSections, setUploadSections] = useState<IDocUploadSection>(initialSections);
	const [deleteFileIndex, setDeleteFileIndex] = useState<string | null>(null);
	const [deleteDocModal, setDeleteDocModal] = useState<IUploadedDocument | null>(null);
	const [isPerformingAction, setIsPerformingAction] = useState<EActionItem>(EActionItem.NULL);

	const getAllEfilingDocuments = async () => {
		try {
			if (currentFilingUUID) {
				const _initialSections = { ...initialSections };
				const docFilingApi = DocFilingApi.getInstance();
				const { records } = await docFilingApi.getFilingDocuments({
					offset: 0,
					count: -1,
					efilingUuid: currentFilingUUID
				});

				if (records?.length > 0) {
					records.forEach((document) => {
						const { metadata, uuid } = document;
						const docKey = metadata?.name;
						if (_initialSections[docKey]) {
							_initialSections[docKey].documents.push({
								uuid: uuid,
								docKey: docKey,
								file: null,
								metadata: {
									key: metadata?.key,
									docKey: metadata?.docKey,
									name: metadata?.name,
									fileName: metadata?.fileName
								}
							});
						}
					});
					setUploadSections(() => _initialSections);
				}
			}
		} catch (error) {
			UtilService.getErrorMessage(error);
		}
	};

	const handleDownloadEfilingDoc = async (downloadFilePath: string) => {
		try {
			if (!currentFilingUUID && downloadFilePath) {
				return;
			}

			const docFilingApi = DocFilingApi.getInstance();
			const response = await docFilingApi.downloadFilingDocument(downloadFilePath);
			window.open(response.data.url, "_blank");
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		}
	};

	const handleDeleteDocument = async () => {
		try {
			if (!deleteDocModal || !currentFilingUUID || !deleteFileIndex) {
				return;
			}

			setIsPerformingAction(EActionItem.DELETE);

			const _deleteDocId: IFilingDeleteDoc = {
				uuids: [deleteDocModal.uuid]
			};

			if (deleteDocModal.uuid) {
				const docFilingApi = DocFilingApi.getInstance();
				await docFilingApi.deleteFilingDocument(_deleteDocId);
			}

			const _uploadSections = { ...uploadSections };
			_uploadSections[deleteDocModal.docKey].documents.splice(Number(deleteFileIndex), 1);
			setUploadSections(() => _uploadSections);
			setDeleteFileIndex(() => null);

			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Deleted",
					message: `${deleteDocModal.metadata?.fileName} deleted successfully`,
					variant: EToastvariant.Success
				})
			);
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		} finally {
			setIsPerformingAction(EActionItem.NULL);
			setDeleteDocModal(null);
		}
	};

	const prepareFileToUpload = (docKey: string, files: File[]) => {
		for (let i = 0; i < files.length; i++) {
			const newDocument: IUploadedDocument = {
				uuid: "",
				docKey: docKey,
				file: files[i],
				metadata: {
					key: "",
					docKey: "",
					name: "",
					fileName: ""
				}
			};

			const _uploadSections = { ...uploadSections };
			_uploadSections[docKey].documents.unshift(newDocument);
			setUploadSections(() => _uploadSections);
		}
	};

	return (
		<>
			{Object.values(uploadSections)?.map((section, index) => (
				<section className="mb-4" key={`filing-docKey-${index}`}>
					<div className="section-title-wrap">
						<h5 className="fw-bold text-secondary">{section.title}</h5>
						{/* Upload files button only visible to ITR file owner */}
						{loggedInUser?.uuid === currentFilingData?.userUuid ? (
							<Button
								variant="link"
								onClick={() => {
									setShowAddDocButton((prev) => ({
										...Object.keys(prev).reduce((acc: any, key) => {
											acc[key] = key === section.docKey ? !prev[key] : false;
											return acc;
										}, {})
									}));
								}}
							>
								Upload Files
							</Button>
						) : null}
					</div>

					<>
						{/* file dropzone open when click on Upload files button */}
						{showAddDocButton[section.docKey] ? (
							<FileUploadDropzone
								size="lg"
								multiple={true}
								allowedFileTypes={[
									".jpg",
									".jpeg",
									".png",
									".pdf",
									".doc",
									".docx",
									".odt",
									".csv",
									".xlsx"
								]}
								handleOnFileSelect={async (files) => {
									setShowAddDocButton((prev) => ({ ...prev, [section.docKey]: false }));
									prepareFileToUpload(section.docKey, files);
								}}
							/>
						) : null}
					</>

					<>
						{section?.documents?.length ? (
							<>
								{/* list of files that are uploaded or ready to upload () */}
								{section?.documents?.map((docObj, index) => (
									<UploadedAndReadyToUploadFiles
										key={`${section.docKey}-${index}`}
										docObj={docObj}
										showDeleteBtn={true}
										handleOnPressDelete={() => {
											setDeleteFileIndex(() => String(index));
											setDeleteDocModal(() => docObj);
										}}
										handleOnPressRemove={() => {
											const _uploadSections = { ...uploadSections };
											_uploadSections[docObj.docKey].documents.splice(Number(index), 1);
											setUploadSections(() => _uploadSections);
										}}
										showDownloadBtn={true}
										handleOnPressDownload={(filePath) => {
											handleDownloadEfilingDoc(filePath);
										}}
										handleOnUploadSuccess={(uploadedDoc) => {
											const _uploadSections = { ...uploadSections };
											_uploadSections[section.docKey].documents.splice(index, 1, uploadedDoc);
											setUploadSections(() => _uploadSections);
										}}
										handleOnUploadError={(uploadedDoc) => {
											const _uploadSections = { ...uploadSections };
											_uploadSections[section.docKey].documents.splice(index, 1, uploadedDoc);
											setUploadSections(() => _uploadSections);
										}}
									/>
								))}
							</>
						) : (
							<>
								{/* only visible to Admin and Expert since they can not upload files for user */}
								{loggedInUser?.uuid !== currentFilingData?.userUuid ? (
									<div className="eds-drag-upload-group">
										<div className="selected-file-wrap">
											<div className="file-info">
												<span className="text-greyDark text-decoration-none h5 mb-0">
													No document uploaded yet.
												</span>
											</div>
										</div>
									</div>
								) : null}
							</>
						)}
					</>
					{index !== Object.values(uploadSections)?.length - 1 && <hr className="my-4" />}
				</section>
			))}

			<ConfirmationModal
				showModal={!!deleteDocModal}
				onYesBtnClick={() => handleDeleteDocument()}
				onNoBtnClick={() => {
					setDeleteDocModal(() => null);
					setDeleteFileIndex(() => null);
				}}
				title="Please confirm"
				message={
					deleteDocModal
						? `Are you sure you want to delete ${deleteDocModal.metadata?.fileName} document?`
						: ""
				}
				onYesBtnText="Confirm"
				onNoBtnText="Cancel"
				isProcessing={isPerformingAction === EActionItem.DELETE}
			/>
		</>
	);
};

export default DocumentsTab;

type UFProps = {
	docObj: IUploadedDocument;
	showDeleteBtn: boolean;
	handleOnPressDelete: () => void;
	handleOnPressRemove: () => void;
	showDownloadBtn: boolean;
	handleOnPressDownload: (filePath: string) => void;
	handleOnUploadSuccess: (uploadedDoc: IUploadedDocument) => void;
	handleOnUploadError: (uploadedDoc: IUploadedDocument) => void;
};

const UploadedAndReadyToUploadFiles: React.FC<UFProps> = (props: UFProps) => {
	const {
		docObj,
		showDeleteBtn,
		handleOnPressDelete,
		handleOnPressRemove,
		showDownloadBtn,
		handleOnPressDownload,
		handleOnUploadSuccess,
		handleOnUploadError
	} = props;

	const dispatch = useAppDispatch();
	const { currentFilingUUID } = useAppSelector(ITRFilingSelector);

	useEffect(() => {
		if (docObj?.uuid) {
			return;
		}

		handleUploadDocument(docObj, docObj.file as File);
	}, [docObj.uuid]);

	const handleUploadDocument = async (docObj: IUploadedDocument, file: File) => {
		try {
			if (!currentFilingUUID) {
				return;
			}

			// on file size error show error message
			if (file.size > FileSizes.MAX_FILE_SIZE) {
				dispatch(
					showToastMsg({
						isVisible: true,
						message: "File size exceeds 20MB. Please choose smaller files.",
						variant: EToastvariant.Danger
					})
				);

				const uploadedDoc: IUploadedDocument = {
					...docObj,
					errorMsg: "File size exceeds 20MB.",
					metadata: {
						...docObj.metadata
					}
				};
				handleOnUploadError(uploadedDoc);
				return;
			}

			const docFilingApi = DocFilingApi.getInstance();
			const formData = new FormData();
			formData.append("file", file);
			const uploadRespData = await docFilingApi.uploadFilingDocument(formData);

			const createFiling = {
				efilingUuid: currentFilingUUID,
				metadata: {
					key: uploadRespData.data.key,
					name: docObj.docKey,
					fileName: file.name
				}
			};
			const createFilingResp = await docFilingApi.createFilingDocument(createFiling);

			const uploadedDoc: IUploadedDocument = {
				...docObj,
				uuid: createFilingResp.data.uuid,
				file: null,
				metadata: {
					...docObj.metadata,
					key: createFilingResp.data.metadata.key,
					docKey: createFilingResp.data.metadata.docKey,
					name: createFilingResp.data.metadata.name,
					fileName: createFilingResp.data.metadata.fileName
				}
			};
			handleOnUploadSuccess(uploadedDoc);

			formData.delete("file");

			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Added",
					message: `${file.name} added successfully`,
					variant: EToastvariant.Success
				})
			);
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);

			const uploadedDoc: IUploadedDocument = {
				...docObj,
				errorMsg: UtilService.getErrorMessage(error),
				metadata: {
					...docObj.metadata
				}
			};
			handleOnUploadError(uploadedDoc);
		} finally {
		}
	};

	return (
		<>
			<UploadedDocument
				file={docObj.file}
				uuid={docObj.uuid}
				fileName={docObj.metadata?.fileName}
				showDeleteBtn={showDeleteBtn}
				handleOnPressDelete={() => handleOnPressDelete()}
				handleOnPressRemove={() => handleOnPressRemove()}
				showDownloadBtn={showDownloadBtn}
				handleOnPressDownload={() => handleOnPressDownload(docObj.metadata.key)}
				errorMsg={docObj.errorMsg}
			/>
		</>
	);
};
